import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Types matching backend entities
export interface ReportConfiguration {
  id: number;
  companyNo: string;
  reportID: number;
  reportType: number;
  reportName: string;
  description: string;
  printerName?: string;
  cloudStorageYesNo: boolean;
  cloudStorageItemLabelFolder?: string;
  externalShare?: string;
  externalFilemask?: string;
  externalPrinterWidth?: number;
  externalPrinterLength?: number;
  externalPrinterLandscape?: boolean;
  updatedBy?: string;
  lastUpdate?: string;
  createdBy?: string;
  created?: string;
}

export interface StandardReportInfo {
  reportName: string;
  reportType: number;
  displayName: string;
  description: string;
  paperWidth: number;
  paperHeight: number;
  landscape: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export enum ReportType {
  ItemLabel = 1,
  ItemAndPurchaseOrder = 2,
  ItemAndSalesOrder = 3,
  LocationLabel = 4,
  LocationLabelAlt = 5,
  External = 99
}

export const ReportTypeLabels: Record<ReportType, string> = {
  [ReportType.ItemLabel]: 'Vare',
  [ReportType.ItemAndPurchaseOrder]: 'Vare og innkjøpsordre',
  [ReportType.ItemAndSalesOrder]: 'Vare og salgsordre',
  [ReportType.LocationLabel]: 'Lokasjon',
  [ReportType.LocationLabelAlt]: 'Lokasjon (alternativ)',
  [ReportType.External]: 'Ekstern'
};

class ReportConfigurationService {
  /**
   * Get all report configurations for current tenant
   */
  async getAll(companyNo: string): Promise<ReportConfiguration[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch report configurations');
    }

    return await response.json();
  }

  /**
   * Get report configuration by ID
   */
  async getById(companyNo: string, id: number): Promise<ReportConfiguration> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch report ${id}`);
    }

    return await response.json();
  }

  /**
   * Get report configurations by type
   */
  async getByType(companyNo: string, reportType: number): Promise<ReportConfiguration[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/type/${reportType}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch reports for type ${reportType}`);
    }

    return await response.json();
  }

  /**
   * Get catalog of all available standard reports
   */
  async getStandardReportCatalog(companyNo: string): Promise<StandardReportInfo[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/standard-catalog`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch standard report catalog');
    }

    return await response.json();
  }

  /**
   * Get standard reports for a specific report type
   */
  async getStandardReportsByType(companyNo: string, reportType: number): Promise<StandardReportInfo[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/standard-catalog/type/${reportType}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch standard reports for type ${reportType}`);
    }

    return await response.json();
  }

  /**
   * Generate preview PDF for a report configuration
   */
  async getPreview(companyNo: string, reportId: number): Promise<Blob> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${reportId}/preview`
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to generate preview: ${error}`);
    }

    return await response.blob();
  }

  /**
   * Generate preview PDF for a standard report by name
   */
  async getStandardReportPreview(companyNo: string, reportName: string): Promise<Blob> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/standard-preview?reportName=${encodeURIComponent(reportName)}`
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to generate standard report preview: ${error}`);
    }

    return await response.blob();
  }

  /**
   * Create new report configuration
   */
  async create(companyNo: string, report: Partial<ReportConfiguration>): Promise<ReportConfiguration> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration`,
      report
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to create report configuration');
    }

    return await response.json();
  }

  /**
   * Update existing report configuration
   */
  async update(companyNo: string, id: number, report: ReportConfiguration): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}`,
      report
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to update report configuration');
    }
  }

  /**
   * Delete report configuration
   */
  async delete(companyNo: string, id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}`
    );

    if (!response.ok) {
      throw new Error('Failed to delete report configuration');
    }
  }

  /**
   * Validate report configuration without saving
   */
  async validate(companyNo: string, report: Partial<ReportConfiguration>): Promise<ValidationResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/validate`,
      report
    );

    if (!response.ok) {
      throw new Error('Failed to validate report configuration');
    }

    return await response.json();
  }
}

export default new ReportConfigurationService();
